import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import LandingCard from '../../../components/Card/LandingCard';
import { EXTERNAL_LINKS } from '../../../app-config';
import { LandingPageDetails } from '../../../interfaces/details';
import GDPRIcon from '../../../assets/icons/GDPR';
import DeveloperIcon from '../../../assets/icons/Developer';
import LibrariesIcon from '../../../assets/icons/Libraries';
import ActiveDot from '../../../assets/icons/ActiveDot';
import NormalDot from '../../../assets/icons/NormalDot';
import usePageStyles from '../styles';

const buildItems: LandingPageDetails[] = [
  {
    index: 0,
    Icon: DeveloperIcon,
    title: 'Personal AI',
    details: [
      'Analyse user data with a proprietary algorithms',
      'Trusted environment to ensure legal compliance',
      'User-authorised access to the generated data',
    ],
    link: EXTERNAL_LINKS.dataswiftPDA,
  },
  {
    index: 1,
    Icon: GDPRIcon,
    title: 'DataPlugs',
    details: [
      'Build your own services that bring data into HAT',
      'Reach wider audience through the data service',
      'Monetise data transfer services',
    ],
    link: EXTERNAL_LINKS.dataswiftBuildPlug,
  },
  {
    index: 2,
    Icon: LibrariesIcon,
    title: 'Libraries',
    details: [
      'Bootstrap your iOS / Android development',
      'JavaScript library for web and Node.js',
      'Scala utility libraries',
    ],
    link: EXTERNAL_LINKS.dataswiftGuide,
  },
];

type IndexType = 0 | 1 | 2;

const getItemsToDisplay = (
  items: LandingPageDetails[],
  index: IndexType,
  isWideScreen: boolean = false,
): LandingPageDetails[] => {
  if (!isWideScreen) return [items[index]];

  switch (index) {
    case 0:
    case 1:
      return [items[index], items[index + 1]];
    case 2:
      return [items[index - 1], items[index]];
  }
};

const isDotActive = (
  index: IndexType,
  currentIndex: IndexType,
  isWideScreen: boolean = false,
): boolean => {
  if (!isWideScreen) return index === currentIndex;

  switch (index) {
    case 0:
      return currentIndex === 0;
    case 1:
      return true;
    case 2:
      return currentIndex === 1 || currentIndex === 2;
  }
};

const BuildForHat: React.FC = () => {
  const matches = useMediaQuery('(min-width: 774px)');
  const [currentIndex, setCurrentIndex] = useState<IndexType>(0);
  const pageClasses = usePageStyles();
  const theme = useTheme();

  const itemsToDisplay = getItemsToDisplay(buildItems, currentIndex, matches);

  return (
    <Grid container className={`${pageClasses.section} ${pageClasses.section2}`}>
      <Grid container justifyContent="center">
        <Grid item>
          <Typography color="textPrimary" variant="h1">
            Build for HAT
          </Typography>
        </Grid>
      </Grid>

      <Grid container justifyContent="center" spacing={3} alignItems="center">
        {matches && (
          <i
            aria-label="Scroll Left"
            className="chevron primary left"
            onClick={() => setCurrentIndex(0)}
          />
        )}

        {itemsToDisplay.map((solution) => (
          <Grid item key={solution.title}>
            <LandingCard {...solution} />
          </Grid>
        ))}

        {matches && (
          <i
            aria-label="Scroll Right"
            className="chevron primary"
            onClick={() => setCurrentIndex(1)}
          />
        )}
      </Grid>

      <Grid container justifyContent="center" alignItems="center">
        <div className={pageClasses.landingScroll}>
          {isDotActive(0, currentIndex, matches) ? (
            <ActiveDot fill={theme.palette.text.primary} />
          ) : (
            <NormalDot
              className="inactive"
              fill={theme.palette.grey[600]}
              onClick={() => setCurrentIndex(0)}
            />
          )}
          {isDotActive(1, currentIndex, matches) ? (
            <ActiveDot fill={theme.palette.text.primary} />
          ) : (
            <NormalDot
              className="inactive"
              fill={theme.palette.grey[600]}
              onClick={() => setCurrentIndex(1)}
            />
          )}
          {isDotActive(2, currentIndex, matches) ? (
            <ActiveDot fill={theme.palette.text.primary} />
          ) : (
            <NormalDot
              className="inactive"
              fill={theme.palette.grey[600]}
              onClick={() => setCurrentIndex(2)}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default BuildForHat;
